import { memo } from 'react'
import cn from 'classnames'

import { Card, HStack, IconButton, Text, VStack } from '~/shared/ui'

import { LunyaAvatar } from '../LunyaAvatar'
import {
  LUNYA_CARD_AVATAR_SIZE_DEFAULT,
  LUNYA_CARD_CLOSE_ICON_SIZE,
  LUNYA_CARD_CLOSE_LABEL,
  LUNYA_CARD_CLOSE_SIZE,
  LUNYA_CARD_TEXT_VARIANT,
} from './config'
import type { LunyaCardProps } from './types'

import styles from './LunyaCard.module.scss'

/**
 * Карточка-реплика Луни (UI-кит §12): мини-Луня слева, опциональный оверлайн и текст.
 * Режим «нежность» — без иронии, без советов; используется на экране человека, в маячке,
 * после отметки «Тяжко».
 *
 * Тема `BLUR` не входит в набор тем базового `Card` (нужен `backdrop-filter`, а не сплошной
 * цвет), поэтому для неё карточка рисует собственный корень тем же радиусом/тенью, что и
 * `Card`, а для `SURFACE`/`ACCENT_WASH` переиспользует `Card` напрямую.
 */
export const LunyaCard = memo(function LunyaCard(props: LunyaCardProps) {
  const {
    text,
    overline,
    avatarSize = LUNYA_CARD_AVATAR_SIZE_DEFAULT,
    theme = 'SURFACE',
    size = 'M',
    onClose,
  } = props

  const content = (
    <div className={styles.body}>
      <div className={styles.text}>
        <HStack gap='SMALL_L' align='START'>
          <LunyaAvatar size={avatarSize} />
          <VStack gap='SMALL_XS'>
            {overline ? (
              <Text variant='OVERLINE' color='ACCENT_SOFT'>
                {overline}
              </Text>
            ) : null}
            <Text variant={LUNYA_CARD_TEXT_VARIANT[size]} color='STRONG'>
              {text}
            </Text>
          </VStack>
        </HStack>
      </div>

      {onClose && (
        <div className={styles.close}>
          <IconButton
            iconName='close'
            size={LUNYA_CARD_CLOSE_SIZE}
            iconSize={LUNYA_CARD_CLOSE_ICON_SIZE}
            variant='GHOST'
            ariaLabel={LUNYA_CARD_CLOSE_LABEL}
            onPress={onClose}
          />
        </div>
      )}
    </div>
  )

  if (theme === 'BLUR') {
    return (
      <div className={cn(styles.root, styles.theme_BLUR, styles[`padding_${size}`])}>{content}</div>
    )
  }

  return (
    <Card theme={theme} padding={size}>
      {content}
    </Card>
  )
})
